<template>
  <div class="StudyConversationMessageEditorTextMobile" :class="classList">
    <div class="StudyConversationMessageEditorTextMobile__content">
      <ValidationProvider name="messageText" rules="required" immediate>
        <LLTextarea
          v-model="localMessage.text"
          size="compact"
          dynamic-size
          menu-bar-style="below"
          :expanded-title="$t('composer_your-response')"
          :expanded-subtitle="questionTitleText"
          submit-on-mod-enter
          :placeholder="localMessage.files.length > 0 ? $t('add_comment_or_send') : $t('type_here')"
          data-e2e="questionInput"
          class="StudyConversationMessageEditorTextMobile__textarea"
          @submit="onSendClick"
        >
          <template v-if="localMessage.files.length" #bottom-content>
            <LLFileSlider
              class="StudyConversationMessageEditorTextMobile__files-slider"
              :files.sync="localMessage.files"
              :editable="true"
            ></LLFileSlider>
          </template>
          <template
            v-if="isFileUploaderVisible || isRoundSendButtonVisible"
            #additional-actions="{ isCapped, expand }"
          >
            <LLFileUploaderWrapper
              v-if="isFileUploaderVisible"
              :with-promise="true"
              :preparable="!localMessage.id"
              :study-id="localMessage.studyId"
              file-mime-type="image/*,image/heic,video/*"
              @upload="onUpload"
              @startUpload="onStartUpload"
              @prepare="onPrepare"
            >
              <LLButton round tertiary>
                <template #icon-left> <PhotoIcon class="text-status-03-600 w-5 h-5" /> </template>
              </LLButton>
            </LLFileUploaderWrapper>
            <span class="StudyConversationMessageEditorTextMobile__spacer"></span>
            <LLButton v-if="isCapped" small tertiary @click="expand">{{ $t('button_expand') }}</LLButton>
            <LLButton
              v-if="isRoundSendButtonVisible"
              round
              primary
              :loading="isSendPending"
              :disabled="!isValid"
              :aria-label="$t('button_send')"
              @click="onSendClick"
            >
              <template #icon-left> <SendIcon /> </template>
            </LLButton>
          </template>
        </LLTextarea>
      </ValidationProvider>
      <LLUploadProgress
        class="StudyConversationMessageEditorTextMobile__file-uploader-progress"
        :uploading-files-uuids.sync="uploadingFilesUuids"
      ></LLUploadProgress>
      <div
        v-if="isAbleToCancel && isActionsVisible"
        class="StudyConversationMessageEditorTextMobile__footer-actions"
      >
        <LLButton
          small
          :loading="isSendPending"
          :disabled="!isValid"
          primary
          class="StudyConversationMessageEditorTextMobile__footer-action"
          @click="onSendClick"
        >
          <template v-if="message.id">{{ $t('button_save') }}</template>
          <template v-else>{{ $t('button_send') }}</template>
        </LLButton>

        <LLButton
          v-if="isAbleToCancel"
          class="StudyConversationMessageEditorTextMobile__footer-action"
          small
          secondary
          :disabled="isSendPending"
          @click="onCancelClick"
          >{{ $t('button_cancel') }}</LLButton
        >
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import {
  StudyConversationMessageOptionsModel,
  StudyConversationMessageType
} from '@/models/studyConversationMessage/studyConversationMessageOptions'
import { StudyConversationMessageModel } from '@/models/studyConversationMessage'
import { QuestionModel } from '@/models/question'
import LLTextarea from '@/components/common/LLTextarea.vue'
import LLFileSlider from '@/components/common/LLFileSlider.vue'
import SendIcon from '@/assets/icons/SendIcon.vue'
import LLButton from '@/components/common/ui-components/LLButton.vue'
import PhotoIcon from '@/assets/icons/PhotoIcon.vue'
import LLFileUploaderWrapper from '@/components/common/LLFileUploader/LLFileUploaderWrapper.vue'
import { ConversationMessageFileModel } from '@/models/conversationMessageFile'
import LLUploadProgress from '@/components/common/LLUploadProgress.vue'

export default {
  name: 'StudyConversationMessageEditorTextMobile',
  components: {
    LLUploadProgress,
    LLFileUploaderWrapper,
    PhotoIcon,
    LLButton,
    SendIcon,
    LLFileSlider,
    LLTextarea,
    ValidationProvider
  },
  props: {
    isCompactLayout: { type: Boolean, default: false },
    isMobileLayout: { type: Boolean, default: false },
    options: {
      type: StudyConversationMessageOptionsModel,
      default: () => new StudyConversationMessageOptionsModel()
    },
    message: { type: StudyConversationMessageModel, required: true },
    question: { type: QuestionModel, required: true },
    isSendPending: { type: Boolean, default: false },
    isValid: { type: Boolean, default: false },
    isAbleToCancel: { type: Boolean, default: false }
  },
  data() {
    return {
      localMessage: null,
      uploadingFilesUuids: [],
      StudyConversationMessageType
    }
  },
  computed: {
    isRoundSendButtonVisible() {
      return !this.isAbleToCancel
    },
    // Mirrors the prototype's fullscreen header subtitle (.fs-head .q), which repeats the
    // question title shown at the top of the card - see #1684.
    questionTitleText() {
      return this.question.title.replace(/(<([^>]+)>)/gi, '')
    },
    classList() {
      return {
        [`${this.$options.name}_old`]: this.message.id,
        [`${this.$options.name}_new`]: !this.message.id
      }
    },
    isActionsVisible() {
      return this.options.type !== StudyConversationMessageType.SCREENING
    },
    isFileUploaderVisible() {
      return this.options.type !== StudyConversationMessageType.SCREENING
    }
  },
  watch: {
    message: {
      handler() {
        if (!this.$_.isEqual(this.message, this.localMessage)) {
          this.localMessage = new StudyConversationMessageModel(this.message)
        }
      },
      deep: true,
      immediate: true
    },
    localMessage: {
      handler() {
        this.$emit('update:message', new StudyConversationMessageModel(this.localMessage))
      },
      deep: true
    }
  },
  methods: {
    onSendClick() {
      this.$emit('sendClick')
    },
    onCancelClick() {
      this.$emit('cancelClick')
    },
    onPrepare(file) {
      this.localMessage.files = [
        ...this.localMessage.files,
        new ConversationMessageFileModel({
          ...file,
          prospectId: this.message.prospectId,
          messageId: this.message.id,
          studyId: this.message.studyId
        })
      ]
    },
    onStartUpload(uploadingFilesUuids) {
      this.uploadingFilesUuids = uploadingFilesUuids
    },
    onUpload(fileList) {
      const convertedFileList = fileList.map(
        (file) =>
          new ConversationMessageFileModel({
            ...file,
            questionId: this.message.questionId,
            prospectId: this.message.prospectId,
            messageId: this.message.id,
            studyId: this.message.studyId
          })
      )
      this.localMessage.files = [...this.localMessage.files, ...convertedFileList]
    }
  }
}
</script>

<style scoped lang="scss">
.StudyConversationMessageEditorTextMobile {
  @apply relative;
  &__content {
    @apply relative;
  }
  &__spacer {
    @apply flex-1;
  }
  &__file-uploader-progress {
    @apply mt-4;
  }
  &__footer-actions {
    @apply mt-4;
  }
  &__footer-action {
    @apply ml-2;
    &:first-child {
      @apply ml-0;
    }
  }
  // No gray card/left-bar wrapper around the composer itself - the redesigned composer's
  // own border and rounded corners are the only framing, matching the prototype - #1684.
  // No margin-top here - StudyConversationStream.vue already adds mt-4 between
  // "Add More" and the composer; adding it here too doubled the gap on mobile - #1684.
}
</style>
